/* ==========================================================================
   ShopSK — Accessibility remediation script (progressive enhancement)
   Runs after DOM is ready. Every block is defensive: it feature-detects and
   no-ops if the target markup isn't present, so it can't break the theme.
   Covers: mega-menu keyboard/ARIA, mobile nav, cart drawer dialog + focus
   trap, FAQ accordions, decorative-icon hiding, icon-only link names,
   aria-hidden focus cleanup, autoplay pause controls, search live region.
   ========================================================================== */
(function () {
  'use strict';

  var FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), ' +
    'select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  function ready(fn) {
    if (document.readyState !== 'loading') { fn(); }
    else { document.addEventListener('DOMContentLoaded', fn); }
  }
  function $(sel, ctx) { return (ctx || document).querySelector(sel); }
  function $all(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  var uid = 0;
  function nextId(p) { uid++; return (p || 'a11y') + '-' + uid; }
  var prefersReduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------------
     1) Decorative icon fonts -> aria-hidden (A-05, KW-006)
     Icon <i>/<span> glyphs with no text become aria-hidden. If the icon is
     the ONLY content of a link/button, give that control an accessible name
     from title/aria-label/data.
     ------------------------------------------------------------------- */
  function fixIcons() {
    $all('i.at, i.ad, i.fa, span.at, span.ad').forEach(function (icon) {
      if (!icon.hasAttribute('aria-hidden')) icon.setAttribute('aria-hidden', 'true');
      icon.setAttribute('focusable', 'false');
    });
    // Icon-only controls need a name
    $all('a, button').forEach(function (el) {
      var text = (el.textContent || '').replace(/\s+/g, '');
      var hasName = el.getAttribute('aria-label') || el.getAttribute('aria-labelledby') || text.length;
      if (hasName) return;
      var name = el.getAttribute('title') || el.getAttribute('data-label');
      if (!name) {
        var img = el.querySelector('img[alt]');
        if (img && img.getAttribute('alt')) name = img.getAttribute('alt');
      }
      if (name) el.setAttribute('aria-label', name.trim());
    });
  }

  /* ---------------------------------------------------------------------
     2) aria-hidden containing focusable descendants (aria-hidden-focus)
     Remove those descendants from the tab order. (Serious, 4.1.2)
     ------------------------------------------------------------------- */
  function fixAriaHiddenFocus() {
    $all('[aria-hidden="true"]').forEach(function (hidden) {
      $all(FOCUSABLE, hidden).forEach(function (f) {
        f.setAttribute('tabindex', '-1');
      });
    });
  }

  /* ---------------------------------------------------------------------
     3) Mega-menu keyboard + ARIA (KW-005, MAN-010, A-04)
     Top-level items that own a submenu become disclosure controls.
     ------------------------------------------------------------------- */
  function enhanceMegaMenu() {
    // Top-level LIs that contain a nested list = have submenu
    var topItems = $all('#main_nav > li.lvl1, .site-nav > li, ul.main-nav > li');
    // De-duplicate
    topItems = topItems.filter(function (el, i) { return topItems.indexOf(el) === i; });

    topItems.forEach(function (li) {
      var submenu = li.querySelector(':scope > div.megamenu, :scope > .dropdown, :scope > ul');
      var trigger = li.querySelector(':scope > a, :scope > span, :scope > button');
      if (!submenu || !trigger) return;
      if (trigger.getAttribute('data-a11y-menu') === '1') return;
      trigger.setAttribute('data-a11y-menu', '1');

      if (!submenu.id) submenu.id = nextId('submenu');
      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', submenu.id);
      // href="#" or empty acts as a toggle, not navigation
      var href = trigger.getAttribute('href');
      if (trigger.tagName === 'A' && (href === '#' || href === '' || href == null)) {
        trigger.setAttribute('role', 'button');
        trigger.setAttribute('tabindex', '0');
      }

      function open() {
        li.classList.add('a11y-open');
        trigger.setAttribute('aria-expanded', 'true');
      }
      function close() {
        li.classList.remove('a11y-open');
        trigger.setAttribute('aria-expanded', 'false');
      }
      function toggle(e) {
        var isOpen = trigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) { close(); } else {
          // close siblings
          topItems.forEach(function (o) {
            var t = o.querySelector('[data-a11y-menu="1"]');
            if (t && t !== trigger) { o.classList.remove('a11y-open'); t.setAttribute('aria-expanded', 'false'); }
          });
          open();
        }
        if (e) { e.preventDefault(); }
      }

      trigger.addEventListener('click', function (e) {
        // Only intercept when it's a pure toggle (no real destination)
        if (trigger.getAttribute('role') === 'button') toggle(e);
      });
      trigger.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          if (trigger.getAttribute('role') === 'button') { toggle(e); }
          else if (e.key !== 'Enter') { toggle(e); }
        } else if (e.key === 'ArrowDown') {
          open();
          var first = submenu.querySelector(FOCUSABLE);
          if (first) { first.focus(); e.preventDefault(); }
        } else if (e.key === 'Escape') {
          close(); trigger.focus();
        }
      });
      // Keyboard within submenu
      submenu.addEventListener('keydown', function (e) {
        var items = $all(FOCUSABLE, submenu);
        var idx = items.indexOf(document.activeElement);
        if (e.key === 'ArrowDown') { e.preventDefault(); (items[idx + 1] || items[0]).focus(); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); (items[idx - 1] || items[items.length - 1]).focus(); }
        else if (e.key === 'Escape') { e.preventDefault(); close(); trigger.focus(); }
        else if (e.key === 'Tab') { /* allow natural tab, but close when leaving */ }
      });
      // Close when focus leaves the whole item
      li.addEventListener('focusout', function () {
        setTimeout(function () {
          if (!li.contains(document.activeElement)) close();
        }, 0);
      });
    });
  }

  /* ---------------------------------------------------------------------
     4) Mobile hamburger toggle (A-04)
     ------------------------------------------------------------------- */
  function enhanceMobileToggle() {
    var toggle = $('.js-mobile-nav-toggle');
    if (!toggle) return;
    var nav = $('#mobileNav, .mobile-nav, #SiteNavMobile, .mob-nav') ||
              document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (nav && !nav.id) nav.id = nextId('mobilenav');
    toggle.setAttribute('aria-haspopup', 'true');
    toggle.setAttribute('aria-expanded', 'false');
    if (nav) toggle.setAttribute('aria-controls', nav.id);
    if (toggle.tagName === 'A') toggle.setAttribute('role', 'button');
    toggle.addEventListener('click', function () {
      var open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  }

  /* ---------------------------------------------------------------------
     5) Cart drawer as an accessible dialog (Cart Flow Deep Dive, A-15)
     role=dialog, aria-modal, labelled by heading, focus move-in + trap,
     Esc to close, restore focus, background inert, trigger aria-expanded.
     ------------------------------------------------------------------- */
  function enhanceCartDrawer() {
    var drawer = $('#cartDrawer, #cart-drawer, .cart-drawer, .mini-cart, #sideCart');
    if (!drawer) return;
    if (!drawer.getAttribute('role')) drawer.setAttribute('role', 'dialog');
    drawer.setAttribute('aria-modal', 'true');
    drawer.setAttribute('tabindex', '-1');
    var heading = drawer.querySelector('h1, h2, h3, h4');
    if (heading) {
      if (!heading.id) heading.id = nextId('cart-title');
      drawer.setAttribute('aria-labelledby', heading.id);
    } else if (!drawer.getAttribute('aria-label')) {
      drawer.setAttribute('aria-label', 'Shopping cart');
    }

    var triggers = $all('.header-cart, .hdicon.header-cart, a[href*="cart"].hdicon, [data-cart-trigger]');
    var lastTrigger = null;
    triggers.forEach(function (t) {
      t.setAttribute('aria-haspopup', 'dialog');
      t.setAttribute('aria-controls', drawer.id || 'cartDrawer');
      t.setAttribute('aria-expanded', 'false');
      t.addEventListener('click', function () { lastTrigger = t; });
    });

    var main = $('#MainContent, main');
    function isOpen() {
      return drawer.classList.contains('open') || drawer.classList.contains('active') ||
             drawer.classList.contains('is-open') || drawer.getAttribute('aria-hidden') === 'false';
    }
    function onOpen() {
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'true'); });
      if (main && main !== drawer && !drawer.contains(main)) { try { main.setAttribute('inert', ''); } catch (e) {} }
      var focusTarget = drawer.querySelector('[aria-label*="lose"], .close-cart') || heading || drawer;
      setTimeout(function () { try { focusTarget.focus(); } catch (e) {} }, 30);
    }
    function onClose() {
      triggers.forEach(function (t) { t.setAttribute('aria-expanded', 'false'); });
      if (main) { try { main.removeAttribute('inert'); } catch (e) {} }
      if (lastTrigger) { try { lastTrigger.focus(); } catch (e) {} }
    }

    // Watch open/close state
    var wasOpen = isOpen();
    var mo = new MutationObserver(function () {
      var now = isOpen();
      if (now && !wasOpen) onOpen();
      if (!now && wasOpen) onClose();
      wasOpen = now;
    });
    mo.observe(drawer, { attributes: true, attributeFilter: ['class', 'aria-hidden', 'style'] });

    // Esc + focus trap while open
    document.addEventListener('keydown', function (e) {
      if (!isOpen()) return;
      if (e.key === 'Escape') {
        var closeBtn = drawer.querySelector('[aria-label*="lose"], .close-cart, .continueCart');
        if (closeBtn) closeBtn.click(); else onClose();
      } else if (e.key === 'Tab') {
        var f = $all(FOCUSABLE, drawer).filter(function (el) { return el.offsetParent !== null; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }

  /* ---------------------------------------------------------------------
     6) FAQ / accordion headings -> real disclosure buttons (A-19)
     Wrap the clickable heading text in a <button aria-expanded aria-controls>.
     ------------------------------------------------------------------- */
  function enhanceAccordions() {
    var heads = $all('.faq-body .p_ttl, .faq .p_ttl, .accordion .p_ttl, .faq-item .question, [data-accordion-trigger]');
    heads.forEach(function (head) {
      if (head.getAttribute('data-a11y-acc') === '1') return;
      head.setAttribute('data-a11y-acc', '1');
      var panel = head.nextElementSibling;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a11y-accordion-trigger';
      btn.innerHTML = head.innerHTML;
      btn.setAttribute('aria-expanded', 'false');
      if (panel) {
        if (!panel.id) panel.id = nextId('acc-panel');
        btn.setAttribute('aria-controls', panel.id);
      }
      head.innerHTML = '';
      head.appendChild(btn);
      btn.addEventListener('click', function () {
        var open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  /* ---------------------------------------------------------------------
     7) Autoplay pause controls (A-10, 2.2.2)
     Adds a Pause/Play button to the marquee info-bar and autoplaying
     slideshows; honors prefers-reduced-motion by starting paused.
     ------------------------------------------------------------------- */
  function addMotionToggles() {
    var targets = [];
    var marquee = $('.marquee-text');
    if (marquee) targets.push(marquee);
    $all('.slideshow[data-slick]').forEach(function (s) {
      try { if (JSON.parse(s.getAttribute('data-slick').replace(/'/g, '"')).autoplay) targets.push(s); }
      catch (e) { targets.push(s); }
    });

    targets.forEach(function (region) {
      if (region.querySelector('.a11y-motion-toggle')) return;
      if (getComputedStyle(region).position === 'static') region.style.position = 'relative';
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'a11y-motion-toggle';
      var paused = prefersReduced;
      function render() {
        btn.innerHTML = paused ? '&#9658;' : '&#10073;&#10073;';
        btn.setAttribute('aria-label', paused ? 'Play moving content' : 'Pause moving content');
      }
      function apply() {
        region.classList.toggle('a11y-paused', paused);
        var slick = region.classList.contains('slideshow') ? region : region.querySelector('.slideshow');
        if (window.jQuery && slick && window.jQuery(slick).hasClass('slick-initialized')) {
          try { window.jQuery(slick).slick(paused ? 'slickPause' : 'slickPlay'); } catch (e) {}
        }
      }
      btn.addEventListener('click', function () { paused = !paused; render(); apply(); });
      render(); apply();
      region.appendChild(btn);
    });
  }

  /* ---------------------------------------------------------------------
     8) Search results live region (A-23, 4.1.3)
     ------------------------------------------------------------------- */
  function enhanceSearchStatus() {
    var results = $('#predictive-search, .predictive-search, .sks-results, #search-results');
    if (!results) return;
    if (!results.getAttribute('aria-live')) {
      results.setAttribute('aria-live', 'polite');
      results.setAttribute('aria-atomic', 'true');
      if (!results.getAttribute('role')) results.setAttribute('role', 'status');
    }
  }

  /* ---------------------------------------------------------------------
     9) Linked background-image tiles -> accessible name (image alt audit)
     Banner/collection tiles are CSS background-images inside links with no
     text, so screen readers hear an empty link. Derive a name from a child
     img alt or from the link's destination (e.g. /collections/johnnie-walker
     -> "Johnnie Walker").
     ------------------------------------------------------------------- */
  function nameLinkedBgTiles() {
    function titleFromHref(href) {
      try {
        var u = new URL(href, location.origin);
        var seg = u.pathname.replace(/\/+$/, '').split('/').filter(Boolean).pop() || '';
        seg = decodeURIComponent(seg).replace(/[-_]+/g, ' ').trim();
        if (!seg || seg === 'collections' || seg === 'pages' || seg === 'products') return '';
        return seg.replace(/\b\w/g, function (c) { return c.toUpperCase(); });
      } catch (e) { return ''; }
    }
    $all('a').forEach(function (a) {
      if (a.getAttribute('aria-label') || (a.textContent || '').trim()) return;
      var hasBg = a.querySelector('.bgImg, [data-bgset], [style*="background-image"]') ||
                  /background-image/.test(a.getAttribute('style') || '');
      if (!hasBg) return;
      var img = a.querySelector('img[alt]');
      var name = (img && img.getAttribute('alt').trim()) || titleFromHref(a.getAttribute('href') || '');
      if (name) a.setAttribute('aria-label', name);
    });
  }

  /* ---------------------------------------------------------------------
     10) Form control labelling safety net (3.3.2 / 4.1.2)
     Guarantees every input/select/textarea has an accessible name so the
     site does not depend on an accessibility overlay. Conservative: only
     touches controls that currently have NO name.
     ------------------------------------------------------------------- */
  function labelFormControls() {
    function named(el){
      if (el.getAttribute('aria-label') || el.getAttribute('aria-labelledby') || el.getAttribute('title')) return true;
      if (el.id) { try { if (document.querySelector('label[for="' + (window.CSS && CSS.escape ? CSS.escape(el.id) : el.id) + '"]')) return true; } catch(e){} }
      if (el.closest && el.closest('label')) return true;
      return false;
    }
    function humanize(s){ return (s||'').replace(/\[.*?\]/g,' ').replace(/[_\-.]+/g,' ').replace(/([a-z])([A-Z])/g,'$1 $2').replace(/\s+/g,' ').trim(); }
    $all('input, select, textarea').forEach(function(el){
      var type = (el.getAttribute('type')||'').toLowerCase();
      if (type==='hidden'||type==='submit'||type==='button'||type==='image'||type==='reset') return;
      if (named(el)) return;
      var name = el.getAttribute('name') || '';
      var cls = el.className || '';
      var label = '';
      if (/quantity|qty|updates\[/i.test(name) || /\bqty\b|quantity/i.test(cls)) label = 'Quantity';
      else if (type==='search' || /(^|[^a-z])q($|[^a-z])|search/i.test(name)) label = 'Search';
      else if (type==='email' || /email/i.test(name)) label = 'Email address';
      else if (type==='tel' || /phone|tel/i.test(name)) label = 'Phone number';
      else if (el.tagName==='SELECT') {
        var wrap = el.closest('.selector-wrapper, .swatch, .product-form__input, .single-option-selector, .variant-input-wrap') || el.parentElement;
        var lg = wrap && wrap.querySelector('label, .form__label, legend, .option-title');
        var t = (lg && lg.textContent.trim()) || '';
        if (!t) {
          var hn = humanize(name);
          if (!hn || hn.length < 3 || /^(id|q|options?)$/i.test(hn)) hn = 'Product options';
          t = hn;
        }
        label = t;
      }
      else label = humanize(name) || (el.getAttribute('placeholder') ? el.getAttribute('placeholder').trim() : '');
      if (label) el.setAttribute('aria-label', label);
    });
    // Radio/checkbox (variant swatches) without a name -> label from adjacent text/value
    $all('input[type="radio"], input[type="checkbox"]').forEach(function(el){
      if (named(el)) return;
      var lab = (el.closest && el.closest('label')) || (el.nextElementSibling && el.nextElementSibling.tagName==='LABEL' ? el.nextElementSibling : null);
      var txt = lab ? lab.textContent.trim() : (el.getAttribute('value')||'');
      if (txt) el.setAttribute('aria-label', txt);
    });
  }

  /* ---------------------------------------------------------------------
     11) Quantity stepper +/- buttons (4.1.2)
     PDP/quantity steppers use <a class="qtyBtn minus|plus"> with CSS-drawn
     icons and no text, so they have no accessible name. Label by class.
     ------------------------------------------------------------------- */
  function labelQtySteppers() {
    $all('.qtyField, .qty, .quantity, .quantity-selector').forEach(function (g) {
      if (!g.getAttribute('role')) { g.setAttribute('role', 'group'); if (!g.getAttribute('aria-label')) g.setAttribute('aria-label', 'Quantity'); }
    });
    $all('.qtyBtn, .qty-btn, .quantity__button').forEach(function (b) {
      if (b.tagName === 'SPAN') return; // the value display
      if (b.getAttribute('aria-label') || b.getAttribute('title')) return;
      var lbl = '';
      if (b.classList.contains('minus') || /minus|decrease|down|dec\b/i.test(b.className)) lbl = 'Decrease quantity';
      else if (b.classList.contains('plus') || /plus|increase|up|inc\b/i.test(b.className)) lbl = 'Increase quantity';
      else { var t = (b.textContent || '').trim(); if (t === '-' || t === '−') lbl = 'Decrease quantity'; else if (t === '+') lbl = 'Increase quantity'; }
      if (lbl) { if (b.tagName === 'A') b.setAttribute('role', 'button'); b.setAttribute('aria-label', lbl); }
    });
  }

  /* ---------------------------------------------------------------------
     12) Product description tabs / accordion (functional + a11y)
     The theme's native toggle for .acor-ttl .tablink is not reliably binding.
     Drive it directly as a single-open accordion (opening one closes the
     others, like tabs) and make it keyboard operable.
     ------------------------------------------------------------------- */
  function enhanceProductTabs() {
    var links = $all('.acor-ttl .tablink, .tbs_lst .tablink, .product-tabs .tablink').filter(function (l) {
      var h = l.getAttribute('href') || '';
      return h.charAt(0) === '#' && h.length > 1 && document.getElementById(h.slice(1));
    });
    if (!links.length) return;
    var entries = links.map(function (link) {
      var panel = document.getElementById(link.getAttribute('href').slice(1));
      var head = link.closest('.acor-ttl') || link.parentElement;
      var group = link.closest('.tbs_lst, .tbs_wrp, .product-tabs, .tabs') || (head && head.parentElement) || document;
      function set(show) {
        panel.style.display = show ? 'block' : 'none';
        link.setAttribute('aria-expanded', show ? 'true' : 'false');
        if (head) { head.classList.toggle('active', show); head.classList.toggle('v18-open', show); }
        panel.classList.toggle('v18-show', show);
        panel.classList.toggle('active', show);
      }
      return { link: link, panel: panel, head: head, group: group, set: set };
    });
    entries.forEach(function (en) {
      if (en.link.getAttribute('data-a11y-tab') === '1') return;
      en.link.setAttribute('data-a11y-tab', '1');
      en.link.setAttribute('role', 'button');
      en.link.setAttribute('aria-controls', en.panel.id);
      en.link.setAttribute('aria-expanded', getComputedStyle(en.panel).display !== 'none' ? 'true' : 'false');
      function activate(e) {
        if (e) { e.preventDefault(); e.stopImmediatePropagation(); }
        var willOpen = en.link.getAttribute('aria-expanded') !== 'true';
        entries.forEach(function (o) { if (o.group === en.group && o !== en) o.set(false); }); // single-open
        en.set(willOpen);
      }
      en.link.addEventListener('click', activate);
      en.link.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') activate(e);
      });
    });
    // default-open the first tab in each group if none is open
    var seen = {};
    entries.forEach(function (en) {
      var k = en.group.id || (en.group.__a11yK || (en.group.__a11yK = 'g' + (++uid)));
      if (seen[k]) return; seen[k] = true;
      var members = entries.filter(function (o) { return o.group === en.group; });
      if (!members.some(function (o) { return o.link.getAttribute('aria-expanded') === 'true'; })) members[0].set(true);
    });
  }

  ready(function () {
    try { fixIcons(); } catch (e) {}
    try { fixAriaHiddenFocus(); } catch (e) {}
    try { enhanceMegaMenu(); } catch (e) {}
    try { enhanceMobileToggle(); } catch (e) {}
    try { enhanceCartDrawer(); } catch (e) {}
    try { enhanceAccordions(); } catch (e) {}
    try { addMotionToggles(); } catch (e) {}
    try { enhanceSearchStatus(); } catch (e) {}
    try { nameLinkedBgTiles(); } catch (e) {}
    try { labelFormControls(); } catch (e) {}
    try { labelQtySteppers(); } catch (e) {}
    try { enhanceProductTabs(); } catch (e) {}
  });
})();
